/**
 * CraftKettle Lightbox
 *
 * General-purpose modal lightbox supporting two modes:
 *
 * Mode 1 — Zoho Quote Form:
 *   Triggered by `.ck-quote-trigger` / `.zf_button_927995` clicks.
 *   Loads Zoho Forms iframe with postMessage height resizing + analytics.
 *   Form: PartnerFormNewLead (Zoho form ID 968962)
 *
 * Mode 2 — Scheduling:
 *   Triggered by clicks on links containing `app.reclaim.ai` or
 *   elements with `.ck-schedule-trigger`.
 *   Loads Reclaim.ai embed URL (meet.reclaimai.com) in an iframe.
 *   Share URLs (app.reclaim.ai) are intercepted and redirected to the embed URL.
 *
 * Shared: accessibility (focus trap, Escape key), quickshop
 * integration via MutationObserver.
 *
 * Trigger classes: .ck-quote-trigger, .ck-schedule-trigger
 * Legacy trigger class: .zf_button_927995 (kept for backward compatibility)
 */

(function () {
  'use strict';

  // --- Configuration ---

  var FORM_URL = 'https://forms.craftkettle.com/craftkettle/form/PartnerFormNewLead/formperma/YWsFRqRjmNcYYoqFbvIc-kNf3q1H3RBAIv3LI_Sjb8M';
  var FORM_PERMA = 'YWsFRqRjmNcYYoqFbvIc-kNf3q1H3RBAIv3LI_Sjb8M';
  var ALLOWED_ORIGINS = ['https://forms.craftkettle.com', 'https://forms.zohopublic.com'];
  var QUOTE_TRIGGER_SELECTOR = '.ck-quote-trigger, .zf_button_927995';
  var SCHEDULE_TRIGGER_SELECTOR = '.ck-schedule-trigger';

  // Reclaim.ai embed URL (iframe-friendly, generated from Reclaim dashboard)
  // Share URLs (app.reclaim.ai/m/...) are intercepted and redirected to this embed URL
  var SCHEDULE_EMBED_URL = 'https://meet.reclaimai.com/e/aba1f348-5130-4779-a8b9-cbfc4c489aee';

  // Google Ads conversion label for lead gen (set after creating conversion action in Google Ads)
  // Instructions: Google Ads > Goals > Conversions > New > Lead Form Submission
  // Copy the conversion label and paste here (format: 'AbCdEf123...')
  var GADS_LEAD_CONVERSION_LABEL = 'VKmFCKif6vkbEL-HzqMD';

  var lightbox = null;
  var iframe = null;
  var previousActiveElement = null;
  var currentMode = null; // 'quote' or 'schedule'
  var keydownBound = false;

  // --- URL Validation ---

  var ALLOWED_SCHEDULE_HOSTS = ['meet.reclaimai.com', 'app.reclaim.ai'];

  function isValidScheduleUrl(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
      if (ALLOWED_SCHEDULE_HOSTS.indexOf(parsed.hostname) === -1) return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  // --- DOM Construction ---

  function buildLightbox() {
    var overlay = document.createElement('div');
    overlay.className = 'ck-lightbox-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Modal');
    overlay.style.display = 'none';

    var dimmer = document.createElement('div');
    dimmer.className = 'ck-lightbox-dimmer';
    dimmer.addEventListener('click', closeLightbox);

    var container = document.createElement('div');
    container.className = 'ck-lightbox-container';

    var closeBtn = document.createElement('button');
    closeBtn.className = 'ck-lightbox-close';
    closeBtn.setAttribute('type', 'button');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    closeBtn.addEventListener('click', closeLightbox);

    var iframeWrap = document.createElement('div');
    iframeWrap.className = 'ck-lightbox-iframe-wrap';

    container.appendChild(closeBtn);
    container.appendChild(iframeWrap);
    overlay.appendChild(dimmer);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    lightbox = overlay;
    return { overlay: overlay, iframeWrap: iframeWrap };
  }

  function createQuoteIframe(iframeWrap) {
    var src = FORM_URL + '?zf_rszfm=1&webpage=' + encodeURIComponent(window.location.href);
    src = appendUtmParams(src);

    var f = document.createElement('iframe');
    f.src = src;
    f.className = 'ck-lightbox-iframe';
    f.setAttribute('frameborder', '0');
    f.setAttribute('title', 'CraftKettle Quote Request Form');
    f.style.border = 'none';
    f.style.width = '100%';
    f.style.overflow = 'hidden';
    f.style.background = 'transparent';

    iframeWrap.innerHTML = '';
    iframeWrap.appendChild(f);
    iframe = f;
  }

  function createScheduleIframe(iframeWrap, url) {
    if (!isValidScheduleUrl(url)) return;

    var f = document.createElement('iframe');
    f.src = url;
    f.className = 'ck-lightbox-iframe';
    f.setAttribute('frameborder', '0');
    f.setAttribute('title', 'Schedule a Meeting with CraftKettle');
    f.setAttribute('allow', 'camera; microphone');
    f.style.border = 'none';
    f.style.width = '100%';
    f.style.minHeight = '600px';
    f.style.background = 'transparent';

    iframeWrap.innerHTML = '';
    iframeWrap.appendChild(f);
    iframe = f;
  }

  // --- UTM Parameter Forwarding ---

  function appendUtmParams(src) {
    try {
      if (typeof ZFAdvLead !== 'undefined' && typeof zfutm_zfAdvLead !== 'undefined') {
        src = appendLeadParams(src, ZFAdvLead, zfutm_zfAdvLead);
      }
      if (typeof ZFLead !== 'undefined' && typeof zfutm_zfLead !== 'undefined') {
        src = appendLeadParams(src, ZFLead, zfutm_zfLead);
      }
    } catch (e) { /* UTM params are optional */ }
    return src;
  }

  function appendLeadParams(src, leadObj, utmObj) {
    for (var i = 0; i < leadObj.utmPNameArr.length; i++) {
      var name = leadObj.utmPNameArr[i];
      var val = utmObj.zfautm_gC_enc
        ? utmObj.zfautm_gC_enc(name)
        : (utmObj.zfutm_gC_enc ? utmObj.zfutm_gC_enc(name) : undefined);
      if (val && val !== '') {
        var sep = src.indexOf('?') > -1 ? '&' : '?';
        src = src + sep + name + '=' + val;
      }
    }
    return src;
  }

  // --- Lightbox Open / Close ---

  function openLightbox(mode, url) {
    var iframeWrap;

    if (!lightbox) {
      var parts = buildLightbox();
      iframeWrap = parts.iframeWrap;
    } else {
      iframeWrap = lightbox.querySelector('.ck-lightbox-iframe-wrap');
      // Destroy existing iframe if switching modes or reopening
      if (iframe) {
        iframe.parentNode.removeChild(iframe);
        iframe = null;
      }
    }

    currentMode = mode;

    // Set aria-label based on mode
    var ariaLabel = mode === 'schedule' ? 'Schedule a meeting' : 'Request a quote';
    lightbox.setAttribute('aria-label', ariaLabel);

    // Create the appropriate iframe
    if (mode === 'schedule') {
      createScheduleIframe(iframeWrap, url);
    } else {
      createQuoteIframe(iframeWrap);
    }

    previousActiveElement = document.activeElement;
    lightbox.style.display = '';
    document.body.style.overflow = 'hidden';

    // Focus the close button for accessibility
    var closeBtn = lightbox.querySelector('.ck-lightbox-close');
    if (closeBtn) closeBtn.focus();

    if (!keydownBound) {
      document.addEventListener('keydown', handleKeydown);
      keydownBound = true;
    }

    // Push dataLayer event for analytics
    if (window.dataLayer) {
      var eventName = mode === 'schedule' ? 'schedule_link_click' : 'quote_form_open';
      window.dataLayer.push({ event: eventName });
    }
  }

  function closeLightbox() {
    if (!lightbox) return;

    lightbox.style.display = 'none';
    document.body.style.overflow = '';
    if (keydownBound) {
      document.removeEventListener('keydown', handleKeydown);
      keydownBound = false;
    }

    // Destroy iframe to reset form/booking state on next open
    if (iframe) {
      iframe.parentNode.removeChild(iframe);
      iframe = null;
    }

    currentMode = null;

    // Restore focus
    if (previousActiveElement) {
      previousActiveElement.focus();
      previousActiveElement = null;
    }
  }

  // --- Keyboard Handling ---

  function handleKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeLightbox();
      return;
    }

    // Focus trap
    if (e.key === 'Tab' || e.keyCode === 9) {
      var focusable = lightbox.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), iframe'
      );
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  // --- Iframe Height Resizing (Zoho postMessage protocol) ---

  function handleIframeMessage(event) {
    if (ALLOWED_ORIGINS.indexOf(event.origin) === -1) return;
    if (currentMode !== 'quote') return;
    if (!event.data || typeof event.data !== 'string') return;

    var parts = event.data.split('|');
    if (parts.length < 2 || parts.length > 3) return;

    var perma = parts[0];
    if (perma !== FORM_PERMA) return;
    if (!iframe) return;

    var newHeight = (parseInt(parts[1], 10) + 15) + 'px';

    if (parts.length === 3) {
      // Form was submitted — scroll iframe into view and fire conversion events
      iframe.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(function () {
        iframe.style.minHeight = newHeight;
      }, 500);

      // Fire analytics events for form submission
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'quote_form_submit' });
      }
      if (typeof gtag === 'function') {
        // GA4 lead gen event
        gtag('event', 'generate_lead', {
          event_category: 'quote',
          event_label: 'zoho_form_submit'
        });
        // Google Ads lead conversion (fires only if label is configured)
        if (GADS_LEAD_CONVERSION_LABEL) {
          gtag('event', 'conversion', {
            send_to: 'AW-879985599/' + GADS_LEAD_CONVERSION_LABEL
          });
        }
      }
    } else {
      iframe.style.minHeight = newHeight;
    }
  }

  // --- Trigger Binding ---

  function bindQuoteTrigger(el) {
    if (el.dataset.ckQuoteBound) return;
    el.dataset.ckQuoteBound = 'true';

    // Add visual styling for button elements
    if (el.tagName === 'BUTTON') {
      el.classList.add('productitem--action-trigger', 'button-secondary');
    }

    el.addEventListener('click', function (e) {
      e.preventDefault();

      if (window.dataLayer) {
        window.dataLayer.push({ event: 'quote_button_click' });
      }

      openLightbox('quote');
    });

    // Keyboard support for role="button" elements (Enter/Space)
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        el.click();
      }
    });
  }

  function bindAllTriggers() {
    var quoteTriggers = document.querySelectorAll(QUOTE_TRIGGER_SELECTOR);
    for (var i = 0; i < quoteTriggers.length; i++) {
      bindQuoteTrigger(quoteTriggers[i]);
    }
  }

  // --- Reclaim.ai Link Interception ---
  // Intercepts clicks on any <a> linking to app.reclaim.ai and opens the embed
  // URL in the lightbox. Share URLs in HTML serve as fallback if JS fails.

  function handleScheduleClick(e) {
    var link = e.target.closest('a[href*="app.reclaim.ai"]');
    if (!link) {
      // Also check for .ck-schedule-trigger class
      var trigger = e.target.closest(SCHEDULE_TRIGGER_SELECTOR);
      if (trigger) {
        e.preventDefault();
        openLightbox('schedule', SCHEDULE_EMBED_URL);
      }
      return;
    }

    e.preventDefault();
    openLightbox('schedule', SCHEDULE_EMBED_URL);
  }

  // --- Quickshop Integration (MutationObserver) ---

  function observeQuickshop() {
    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType !== 1) continue;
          // Check if the added node itself is a quote trigger
          if (added[j].matches && added[j].matches(QUOTE_TRIGGER_SELECTOR)) {
            bindQuoteTrigger(added[j]);
          }
          // Check descendants
          var nested = added[j].querySelectorAll
            ? added[j].querySelectorAll(QUOTE_TRIGGER_SELECTOR)
            : [];
          for (var k = 0; k < nested.length; k++) {
            bindQuoteTrigger(nested[k]);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // --- Init ---

  function init() {
    bindAllTriggers();
    observeQuickshop();
    window.addEventListener('message', handleIframeMessage, false);

    // Delegated click handler for Reclaim.ai scheduling links
    document.addEventListener('click', handleScheduleClick, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
